import React, { useRef, useState, useEffect } from "react";
import "./AddProduct.css";
import { BiArrowBack } from "react-icons/bi";
import InfoSection from "./Sections/InfoSection";
import MediaSection from "./Sections/MediaSection";
import PricingSection from "./Sections/PricingSection";
import VariantSection from "./Sections/VariantsSection";
import TableOfContent from "../Components/TableOfContent/TableOfContent";
import StatusSection from "./Sections/StatusSection";
import CategorySection from "./Sections/CategorySection";
import { Link, useNavigate, useLocation } from "react-router-dom";
import IdentifiersSection from "./Sections/IdentifiersSection";
import DimensionsSection from "./Sections/DimensionsSection";
import ComboSection from "./Sections/ComboSection";
import { useSelector, useDispatch } from "react-redux";
import {
  createProduct,
  updateProduct,
  fetchRawProductData,
} from "../../../services/product.service";
import CircularProgress from "@mui/material/CircularProgress";
import { showSnackBar } from "../../../store/common/snackBarSlice";
import { useTranslation } from "react-i18next";
import i18n from "../../../i18n";

const initialContentList = [
  {
    heading: "Table of Content",
    i18nKey: "productForm.tableOfContent",
    menus: [
      { name: "Basic Information", i18nKey: "productForm.basicInformation", id: "addproduct_basic_info" },
      { name: "Product's Media", i18nKey: "productForm.productsMedia", id: "addproduct_media" },
      { name: "Product's Pricing", i18nKey: "productForm.productsPricing", id: "addproduct_pricing" },
      { name: "Dimensions", i18nKey: "productForm.dimensions", id: "addproduct_dimensions" },
      { name: "Identifiers", i18nKey: "productForm.identifiers", id: "addproduct_identifiers" },
      { name: "Product's Combo", i18nKey: "productForm.productsCombo", id: "addproduct_combosection" },
      { name: "Variants", i18nKey: "productForm.variants", id: "addproduct_variants" },
    ],
  },
];

const initialFormData = {
  code: "",
  category: "",
  attributes: [],
  variants: [],
  title: "",
  description: "",
  images: [],
  media: null,
  main_image: {},
  regular_price: 0,
  warehouse_ids: [],
  sales_price: 0,
  suppliers: [],
  length: "",
  width: "",
  height: "",
  weight: "",
  product_identifiers: [],
  status: "Active",
  type: "",
  brand: "",
  currency: "cad",
  dimension_unit: "",
  weight_unit: "",
  product_combo: [],
  sku: "",
  additional_information: "",
  deletedMediaIds: [],
  deletedVariationIds: [],
  deletedAttributeIds: [],
  deletedSuppliersIds: [],
  deletedAdditionalInformationIds: [],
};

const initialProductIdentifier = { marketplace: "", sku: "", slug: "" };

const marketplaces = [
  { id: 1, title: "Amazon" },
  { id: 2, title: "Woocommerce" },
  { id: 3, title: "Ebay" },
  { id: 4, title: "Walmart" },
  { id: 5, title: "Shopify" },
  { id: 6, title: "Best Buy" },
];

const ProductForm = () => {
  const { t } = useTranslation(["inventory", "common"]);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);

  const editData = location.state;
  const editMode = !!editData;

  const [attributes, setAttributes] = useState([]);
  const [variationAttributes, setVariationAttributes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [productTypes, setProductTypes] = useState();
  const [productBrands, setProductBrands] = useState();
  const [productSuppliers, setProductSuppliers] = useState();
  const [variants, setVariants] = useState([]);
  const [contentList, setContentList] = useState(initialContentList);
  const [errors, setErrors] = useState({});
  const [uploadedImages, setUploadedImages] = useState([]);
  const [savingProduct, setSavingProduct] = useState(false);
  const [additionalFields, setAdditionalFields] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedSubCategory, setSelectedSubCategory] = useState("");
  const [selectedType, setSelectedType] = useState("");
  const [selectedBrand, setSelectedBrand] = useState("");
  const [packageDetails, setPackageDetails] = useState([]);
  const [productIdentifiers, setProductIdentifiers] = useState([
    initialProductIdentifier,
  ]);
  const [supplierSections, setSupplierSections] = useState([]);
  const [formData, setFormData] = useState(initialFormData);

  useEffect(() => {
    if (editMode && editData) {
      setFormData({
        sku: editData?.product_sku ?? "",
        id: editData?.id,
        category: {
          value: editData?.category?.id,
          title: editData?.category?.name,
        },
        attributes: [],
        code: editData?.code ?? "",
        title: editData?.name ?? "",
        description: editData?.description ?? "",
        media: editData?.media ?? [],
        main_image: {},
        regular_price: editData?.regular_price ?? 0,
        sales_price: editData?.sale_price ?? 0,
        suppliers: editData?.product_suppliers?.map(s => ({
          supplier: { value: s.suppliers?.id, title: s.suppliers?.name },
          cost: s.cost,
          profit: "",
          margin: ""
        })) || [],
        length: editData?.length ?? "",
        width: editData?.width ?? "",
        height: editData?.height ?? "",
        weight: editData?.weight ?? "",
        additional_information: editData?.additional_informations ?? "",
        deletedMediaIds: [],
        deletedVariationIds: [],
        deletedAttributeIds: [],
        deletedSuppliersIds: [],
        deletedAdditionalInformationIds: [],
        product_identifiers: [],
        status: editData?.status ?? "",
        type: editData?.type
          ? { title: editData?.type?.name, value: editData?.type?.id }
          : "",
        brand: {
          value: editData?.brand?.id,
          title: editData?.brand?.name,
        },
        currency: "cad",
        warehouse_ids: (editData?.product_warehouse || []).flatMap(
          (warehouse) => {
            const warehouseId = warehouse?.warehouse_id;
            if (typeof warehouseId === "string") return warehouseId.split(",");
            if (typeof warehouseId === "number") return [String(warehouseId)];
            return [];
          }
        ),
        package_details: editData?.package_details
          ? Object.values(editData?.package_details)
          : [],
        dimension_unit: editData?.dimension_unit
          ? {
              title: editData?.dimension_unit === "in" ? i18n.t("inventory:productForm.inchIn") : i18n.t("inventory:productForm.centemeterCm"),
              value: editData?.dimension_unit,
            }
          : "",
        weight_unit: editData?.weight_unit
          ? {
              title: editData?.weight_unit === "kg" ? i18n.t("inventory:productForm.kilogramKg") : i18n.t("inventory:productForm.poundLb"),
              value: editData?.weight_unit,
            }
          : "",
        variants: editData?.children?.map((variant) => ({
          ...variant,
          variant: variant.name,
          sku: variant.product_sku,
          regular_price: variant.regular_price ?? 0,
          sales_price: variant.sale_price ?? 0,
          dimension_unit: variant.dimension_unit ? {
            title: variant.dimension_unit === "in" ? i18n.t("inventory:productForm.inchIn") : i18n.t("inventory:productForm.centemeterCm"),
            value: variant.dimension_unit,
          } : "",
          weight_unit: variant.weight_unit ? {
            title: variant.weight_unit === "kg" ? i18n.t("inventory:productForm.kilogramKg") : i18n.t("inventory:productForm.poundLb"),
            value: variant.weight_unit,
          } : "",
          product_identifiers: variant?.product_skus?.map(ident => ({
            ...ident,
            marketplace: marketplaces.find(m => `${m.id}` === ident.market_place_id)
          })) || [],
          suppliers: variant?.product_suppliers?.map(s => ({
            supplier: { value: s.suppliers?.id, title: s.suppliers?.name },
            unit_cost: s.cost
          })) || []
        })),
      });

      setVariants(
        editData?.children?.map((variant) => ({
          ...variant,
          variant: variant.name,
          sku: variant.product_sku,
          regular_price: variant.regular_price ?? 0,
          sales_price: variant.sale_price ?? 0,
          dimension_unit: variant.dimension_unit ? {
            title: variant.dimension_unit === "in" ? i18n.t("inventory:productForm.inchIn") : i18n.t("inventory:productForm.centemeterCm"),
            value: variant.dimension_unit,
          } : "",
          weight_unit: variant.weight_unit ? {
            title: variant.weight_unit === "kg" ? i18n.t("inventory:productForm.kilogramKg") : i18n.t("inventory:productForm.poundLb"),
            value: variant.weight_unit,
          } : "",
          product_identifiers: variant?.product_skus?.map(ident => ({
            ...ident,
            marketplace: marketplaces.find(m => `${m.id}` === ident.market_place_id)
          })) || [],
          suppliers: variant?.product_suppliers?.map(s => ({
            supplier: { value: s.suppliers?.id, title: s.suppliers?.name },
            unit_cost: s.cost
          })) || []
        }))
      );

      setUploadedImages(editData?.media ?? []);
      setAttributes(
        editData?.attributes?.map((attr) => ({
          ...attr,
          value: attr?.value.split(","),
        })) || []
      );
      setSelectedCategory({
        title: editData?.category?.name,
        value: editData?.category?.id,
      });
      setSelectedBrand({
        title: editData?.brand?.name,
        value: editData?.brand?.id,
      });
      setSelectedType({
        title: editData?.type?.name,
        value: editData?.type?.id,
      });
      if (editData?.product_skus) {
        setProductIdentifiers(editData?.product_skus?.map(ident => ({
          ...ident,
          marketplace: marketplaces.find(m => `${m.id}` === ident.market_place_id)
        })));
      }
      if (editData?.product_suppliers) {
        setSupplierSections(editData?.product_suppliers?.map(s => ({
          supplier: { value: s.suppliers?.id, title: s.suppliers?.name },
          cost: s.cost
        })));
      }
      if (editData?.package_details) {
          setPackageDetails(Object.values(editData.package_details));
      }
      if (editData?.additional_informations) {
        setAdditionalFields(editData.additional_informations);
      }
    }
  }, [editMode, editData]);

  function transformMediaObject(inputObject) {
    const transformedObject = { ...inputObject };
    delete transformedObject.created_at;
    delete transformedObject.updated_at;
    delete transformedObject.product_id;
    for (const key in transformedObject) {
      if (transformedObject[key] === "true") transformedObject[key] = true;
      else if (transformedObject[key] === "false") transformedObject[key] = false;
    }
    return transformedObject;
  }

  function navigateToSection(data) {
    let sectionId = null;
    if (data.title || data.description || data.code) sectionId = "addproduct_basic_info";
    else if (data.regular_price || data.sales_price) sectionId = "addproduct_pricing";
    else if (data.length || data.width || data.height || data.weight || data.unit) sectionId = "addproduct_dimensions";
    else if (data.children) sectionId = "addproduct_variants";
    else if (data.category || data.sub_category || data.type) sectionId = "addproduct_basic_category";
    else if (data.status) sectionId = "addproduct_basic_status";
    if (sectionId) window.location.hash = sectionId;
    return sectionId;
  }

  function processErrorResponse(errorResponse) {
    const { message, success, request, ...rest } = errorResponse;
    const result = {};
    for (const key in rest) {
      if (Array.isArray(rest[key]) && rest[key].length > 0) {
        result[key] = { msg: rest[key][0] };
      }
    }
    navigateToSection(result);
    return result;
  }

  const fieldsToCheck = [
    "title", "code", "category", "brand", "dimension_unit", "weight_unit",
    "media", "regular_price", "warehouse_ids", "sku",
  ];

  function checkEmptyFields(inputObject, fieldsToValidate) {
    const emptyFields = {};
    function checkFieldsRecursive(obj, path = "") {
      for (const key in obj) {
        const fieldPath = path ? `${path}.${key}` : key;
        const value = obj[key];
        if (fieldsToValidate.includes(fieldPath)) {
          if (value === null || value === undefined || value === "" || value?.length === 0) {
            emptyFields[fieldPath] = { msg: t("productForm.fieldIsRequired") };
          }
        }
        if (key === "children" && Array.isArray(value)) {
            value.forEach((child, index) => {
              const childPath = `${fieldPath}[${index}]`;
              if (!child.code) {
                emptyFields[`${childPath}.code`] = { msg: t("productForm.skuIsRequiredForEachVariant") };
              }
              checkFieldsRecursive(child, childPath);
            });
          } else if (typeof value === "object" && value !== null) {
            checkFieldsRecursive(value, fieldPath);
          }
      }
    }
    checkFieldsRecursive(inputObject);
    navigateToSection(emptyFields);
    setErrors(emptyFields);
    return Object.keys(emptyFields).length === 0;
  }

  const createFormData = (payload) => {
    const fd = new FormData();
    const replaceUndefined = (val) => (val === undefined ? "" : val);

    const appendToFD = (key, value) => {
      value = replaceUndefined(value);
      if (Array.isArray(value)) {
        value.forEach((item, index) => {
          if (typeof item === "object" && item !== null && !(item instanceof File) && !(item instanceof Blob)) {
            for (const itemKey in item) {
              fd.append(`${key}[${index}][${itemKey}]`, replaceUndefined(item[itemKey]));
            }
          } else {
            fd.append(`${key}[${index}]`, replaceUndefined(item));
          }
        });
      } else if (typeof value === "object" && value !== null && !(value instanceof File) && !(value instanceof Blob)) {
        for (const nestedKey in value) {
          fd.append(`${key}[${nestedKey}]`, replaceUndefined(value[nestedKey]));
        }
      } else {
        fd.append(key, value);
      }
    };

    for (const key in payload) {
      if (key === "product_variations") {
        payload[key].forEach((variation, index) => {
          for (const vKey in variation) {
            appendToFD(`product_variations[${index}][${vKey}]`, variation[vKey]);
          }
        });
      } else {
        appendToFD(key, payload[key]);
      }
    }
    return fd;
  };

  const handleSaveProduct = async () => {
    let valid = checkEmptyFields(formData, fieldsToCheck);
    
    if (formData?.variants?.length > 0) {
      let variantError = false;
      formData?.variants.forEach((variant, index) => {
        if (!variant?.sku?.trim()) {
          variantError = t("productForm.variantSkuRequired", { number: index + 1, name: variant.variant });
        } else if (!variant?.code?.trim()) {
          variantError = t("productForm.variantCodeRequired", { number: index + 1, name: variant.variant });
        }
      });
      if (variantError) {
        dispatch(showSnackBar({ text: variantError, severity: "error" }));
        return;
      }
    }

    if (!valid) return;

    const payload = {
      product_sku: formData?.sku,
      code: formData?.code,
      name: formData?.title,
      description: formData?.description,
      additional_information: formData?.additional_information,
      product_variation_attribute_id: "null",
      full_name: formData?.title,
      warehouse_ids: formData?.warehouse_ids,
      category_id: formData?.category?.value ?? "",
      subcategory_id: formData?.sub_category?.value ?? "",
      type: formData?.type?.value ?? "",
      length: formData?.length,
      width: formData?.width,
      height: formData?.height,
      weight: formData?.weight,
      product_type_id: formData?.type?.value ?? "",
      product_unit_id: "",
      brand_id: formData?.brand?.value ?? "",
      regular_price: formData?.regular_price,
      sale_price: formData?.sales_price,
      currency: formData?.currency,
      dimension_unit: formData?.dimension_unit?.value ?? "",
      weight_unit: formData?.weight_unit?.value ?? "",
      attributes,
      has_variants: `${formData.variants?.length > 0}`,
      package_details: editMode ? JSON.stringify(formData?.package_details) : (formData?.package_details ?? ""),
      fulfillment_cost: "",
      product_identifiers: formData?.identifiers?.map((identifier) => ({
        market_place_id: identifier.marketplace.id,
        sku: identifier.sku,
        slug: identifier.slug,
      })),
      product_suppliers: formData?.suppliers?.map((supplier) => ({
        cost: supplier.cost,
        supplier_id: supplier.supplier?.value ?? "",
      })),
      product_combo: formData?.type?.title?.toLowerCase() === "combo" ? formData?.product_combo : [],
      media: editMode ? formData?.media?.map(transformMediaObject) : formData?.media,
      product_variations: formData.variants?.length > 0 && formData?.type?.title?.toLowerCase() !== "combo" ? formData.variants.map((v) => ({
        id: v.id ?? "",
        code: v.code,
        product_sku: v.sku,
        name: v.variant,
        full_name: `${formData.title ? formData.title + " - " : ""}${v.variant}`,
        category_id: formData?.category?.value,
        subcategory_id: "",
        length: v.length,
        width: v.width,
        height: v.height,
        weight: v.weight,
        regular_price: v.regular_price,
        sale_price: v.sales_price,
        dimension_unit: v.dimension_unit?.value ?? formData?.dimension_unit?.value ?? "",
        weight_unit: v.weight_unit?.value ?? formData?.weight_unit?.value ?? "",
        warehouse_ids: formData?.warehouse_ids,
        product_type_id: "",
        product_unit_id: "",
        brand_id: formData?.brand?.value ?? "",
        fulfillment_cost: "",
        deletedMediaIds: v.deletedMediaIds ?? [],
        deletedSuppliersIds: v.deletedSuppliersIds ?? [],
        product_identifiers: v?.product_identifiers?.map((identifier) => ({
          market_place_id: identifier?.marketplace?.id || identifier?.market_place_id,
          sku: identifier.sku,
          slug: identifier.slug,
        })),
        media: v.media?.map(img => (editMode ? transformMediaObject(img) : img)),
        product_suppliers: v?.suppliers?.map((supplier) => ({
          cost: supplier.unit_cost || supplier.cost,
          supplier_id: supplier.supplier?.value,
        })),
      })) : [],
    };

    if (editMode) {
      payload.deletedVariationIds = formData?.deletedVariationIds;
      payload.deletedAttributeIds = formData?.deletedAttributeIds;
      payload.deletedMediaIds = formData?.deletedMediaIds ?? [];
      payload.deletedSuppliersIds = formData?.deletedSuppliersIds ?? [];
      payload.deletedAdditionalInformationIds = formData?.deletedAdditionalInformationIds;
    }

    const formDataPayload = createFormData(payload);
    try {
      setSavingProduct(true);
      const response = editMode 
        ? await updateProduct(formDataPayload, formData.id)
        : await createProduct(formDataPayload);

      if (!response?.error) {
        dispatch(showSnackBar({ text: editMode ? t("productForm.productUpdatedSuccessfully") : t("productForm.productCreatedSuccessfully"), severity: "success" }));
        navigate("/products");
      } else {
        setErrors(processErrorResponse(response?.error?.response?.data));
        dispatch(showSnackBar({ text: response?.error?.response?.data?.message || t("common:messages.somethingWentWrong"), severity: "error" }));
      }
    } catch (error) {
      dispatch(showSnackBar({ text: t("common:messages.somethingWentWrong"), severity: "error" }));
    } finally {
      setSavingProduct(false);
    }
  };

  const fetchInitialData = async () => {
    try {
      const response = await fetchRawProductData();
      if (!response.error) {
        setVariationAttributes(response?.data?.result?.allVariations?.map(a => ({ value: a.id, title: a.name })));
        setCategories(response?.data?.result?.categories?.map(c => ({ value: c.id, title: c.name })));
        setProductTypes(response?.data?.result?.productTypes?.map(t => ({ value: t.id, title: t.name })));
        setProductBrands(response?.data?.result?.brands?.map(b => ({ value: b.id, title: b.name })));
        setProductSuppliers(response?.data?.result?.suppliers?.map(s => ({ value: s.id, title: s.name })));
        if (!editMode) {
            setSelectedType({ value: response?.data?.result?.productTypes[0].id, title: response?.data?.result?.productTypes[0].name });
        }
      }
    } catch (error) {}
  };

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (formData?.type?.id === 2 || formData?.type?.title?.toLowerCase() === "combo") {
      setContentList(initialContentList);
    } else {
      setContentList((prevList) => {
        const menus = prevList[0].menus?.filter(
          (item) => item?.id !== "addproduct_combosection"
        );
        return [
          {
            ...prevList[0],
            menus,
          },
        ];
      });
    }
  }, [formData?.type]);

  return (
    <div className="add__prod_screen">
      <div className="add__prod_screenleft">
        <TableOfContent contentList={contentList} />
      </div>
      <div className="add__prod_screencenter">
        <div className="add__prod_sheader hide_mobile_btn">
          <Link to={user.role === "Shipper" ? "/products" : "/warehouse-products"}>
            <BiArrowBack size={22} className="add__prod_sheaderback" />
          </Link>
          {editMode ? t("productForm.editProductTitle") : t("productForm.addProduct")}
        </div>
        <InfoSection formData={formData} setFormData={setFormData} errors={errors} setErrors={setErrors} additionalFields={additionalFields} setAdditionalFields={setAdditionalFields} editMode={editMode} />
        <MediaSection formData={formData} setFormData={setFormData} errors={errors} setErrors={setErrors} uploadedImages={uploadedImages} setUploadedImages={setUploadedImages} />
        <PricingSection formData={formData} setFormData={setFormData} errors={errors} setErrors={setErrors} productSuppliers={productSuppliers} setSupplierSections={setSupplierSections} supplierSections={supplierSections} />
        <DimensionsSection formData={formData} setFormData={setFormData} errors={errors} setErrors={setErrors} packageDetails={packageDetails} setPackageDetails={setPackageDetails} />
        <IdentifiersSection formData={formData} setFormData={setFormData} errors={errors} setErrors={setErrors} productIdentifiers={productIdentifiers} setProductIdentifiers={setProductIdentifiers} />
        {formData?.type?.title?.toLowerCase() === "combo" && <ComboSection formData={formData} setFormData={setFormData} errors={errors} setErrors={setErrors} />}
        {formData?.type?.title?.toLowerCase() !== "combo" && (
          <VariantSection attributes={attributes} setAttributes={setAttributes} variants={variants} setVariants={setVariants} formData={formData} setFormData={setFormData} variationAttributes={variationAttributes} setVariationAttributes={setVariationAttributes} productSuppliers={productSuppliers} editMode={editMode} />
        )}
        <div className="add__prod_bottomsec">
          <button className="add__prod_saveprodbtn" onClick={handleSaveProduct} disabled={savingProduct}>
            {!savingProduct ? t("common:actions.save") : <CircularProgress color="inherit" size={16} />}
          </button>
        </div>
      </div>
      <div className="add__prod_screenright">
        <div className="add__prod_sheader show_mobile_btn">
            <Link to={user.role === "Shipper" ? "/products" : "/warehouse-products"}>
                <BiArrowBack size={22} className="add__prod_sheaderback" />
            </Link>
            {editMode ? t("productForm.editProductTitle") : t("productForm.addProduct")}
        </div>
        <StatusSection formData={formData} setFormData={setFormData} errors={errors} setErrors={setErrors} />
        <CategorySection formData={formData} productTypes={productTypes} productBrands={productBrands} setProductBrands={setProductBrands} categories={categories} setCategories={setCategories} setFormData={setFormData} errors={errors} setErrors={setErrors} selectedCategory={selectedCategory} setSelectedCategory={setSelectedCategory} selectedSubCategory={selectedSubCategory} setSelectedSubCategory={setSelectedSubCategory} selectedType={selectedType} setSelectedType={setSelectedType} selectedBrand={selectedBrand} setSelectedBrand={setSelectedBrand} />
      </div>
    </div>
  );
};

export default ProductForm;
